import { CMS_BASE_URL } from './constants'
import { buildStrapiRequestHeaders, buildStrapiRequestUrl } from './strapiRequest'
import {
  extractStrapiMediaUrl,
  readString,
  toStrapiEntity,
  toStrapiEntityCollection,
} from './utils/strapiPayload'

import type {
  SdkCtaButtonCmsContent,
  SdkFeatureCmsContent,
  SdkGetStartedStepCmsContent,
  SdkPageCmsContent,
  SdkResourceCardCmsContent,
  SdkSystemRequirementCmsContent,
} from './interfaces'

interface FetchSdkContentOptions {
  signal?: AbortSignal
  previewToken?: string
}

const SDK_PAGE_ENDPOINT = `/api/sdk-page?${
  [
    'populate[customSolutionsImageDesktop]=true',
    'populate[customSolutionsImageTablet]=true',
    'populate[customSolutionsImageMobile]=true',
    'populate[customSolutionsFeatures][populate][icon][fields][0]=url',
    'populate[codeBlockButton]=true',
    'populate[getStartedSteps][populate][icon][fields][0]=url',
    'populate[systemRequirements][populate][items]=true',
    'populate[resourceCards][populate][icon][fields][0]=url',
  ].join('&')
}`

const readMediaUrl = (value: unknown): string | null => extractStrapiMediaUrl(value, CMS_BASE_URL)

const readOptionalString = (value: unknown): string | undefined => {
  const text = readString(value)

  return text ?? undefined
}

const mapCtaButton = (value: unknown): SdkCtaButtonCmsContent | null => {
  const button = toStrapiEntity(value)
  const text = readString(button?.label)
  const to = readString(button?.url)

  if (text === null || to === null) {
    return null
  }

  return { text, to }
}

const mapFeatureCards = (value: unknown): SdkFeatureCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((card): SdkFeatureCmsContent[] => {
      const iconUrl = readMediaUrl(card.icon)
      const title = readString(card.title)
      const description = readString(card.body)

      if (iconUrl === null || title === null || description === null) {
        return []
      }

      return [{ iconUrl, title, description }]
    })
)

const mapGetStartedSteps = (value: unknown): SdkGetStartedStepCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((step): SdkGetStartedStepCmsContent[] => {
      const iconUrl = readMediaUrl(step.icon)
      const text = readString(step.text)

      if (iconUrl === null || text === null) {
        return []
      }

      return [{
        iconUrl,
        text,
        desktopText: readOptionalString(step.desktopText),
        url: readOptionalString(step.url),
      }]
    })
)

const mapSystemRequirements = (value: unknown): SdkSystemRequirementCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((requirement): SdkSystemRequirementCmsContent[] => {
      const title = readString(requirement.title)
      const items = toStrapiEntityCollection(requirement.items)
        .map((item) => readString(item.text))
        .filter((item): item is string => item !== null)

      if (title === null || items.length === 0) {
        return []
      }

      return [{ title, items }]
    })
)

const mapResourceCards = (value: unknown): SdkResourceCardCmsContent[] => (
  toStrapiEntityCollection(value)
    .flatMap((card): SdkResourceCardCmsContent[] => {
      const iconUrl = readMediaUrl(card.icon)
      const text = readString(card.title)
      const buttonText = readString(card.ctaLabel)
      const href = readString(card.ctaUrl)

      if (iconUrl === null || text === null || buttonText === null || href === null) {
        return []
      }

      return [{ iconUrl, text, buttonText, href }]
    })
)

const mapSdkPageContent = (payload: unknown): SdkPageCmsContent | null => {
  const payloadRoot = toStrapiEntity(payload)
  const contentRoot = toStrapiEntity(payloadRoot?.data || payloadRoot)

  if (!contentRoot) {
    return null
  }

  const heroPillText = readString(contentRoot.heroPillText)
  const heroTitleText = readString(contentRoot.heroTitleText)
  const heroDescription = readString(contentRoot.heroDescription)

  const customSolutionsTitle = readString(contentRoot.customSolutionsTitle)
  const customSolutionsSubtitle = readString(contentRoot.customSolutionsSubtitle)
  const customSolutionsImageDesktopUrl = readMediaUrl(contentRoot.customSolutionsImageDesktop)
  const customSolutionsImageTabletUrl = readMediaUrl(contentRoot.customSolutionsImageTablet)
  const customSolutionsImageMobileUrl = readMediaUrl(contentRoot.customSolutionsImageMobile)
  const customSolutionsFeatures = mapFeatureCards(contentRoot.customSolutionsFeatures)

  const codeBlockHeading = readString(contentRoot.codeBlockHeading)
  const codeBlockBody = readString(contentRoot.codeBlockBody)
  const codeBlockButton = mapCtaButton(contentRoot.codeBlockButton)

  const getStartedTitle = readString(contentRoot.getStartedTitle)
  const getStartedSteps = mapGetStartedSteps(contentRoot.getStartedSteps)
  const systemRequirements = mapSystemRequirements(contentRoot.systemRequirements)
  const resourceCards = mapResourceCards(contentRoot.resourceCards)

  const exploreSoundcoreTitle = readString(contentRoot.exploreSoundcoreTitle)
  const exploreSoundcoreButtonText = readString(contentRoot.exploreSoundcoreButtonText)
  const exploreSoundcoreButtonUrl = readString(contentRoot.exploreSoundcoreButtonUrl)
  const exploreSoundscapeTitle = readString(contentRoot.exploreSoundscapeTitle)
  const exploreSoundscapeButtonText = readString(contentRoot.exploreSoundscapeButtonText)
  const exploreSoundscapeButtonUrl = readString(contentRoot.exploreSoundscapeButtonUrl)

  if (
    heroPillText === null ||
    heroTitleText === null ||
    heroDescription === null ||
    customSolutionsTitle === null ||
    customSolutionsSubtitle === null ||
    customSolutionsImageDesktopUrl === null ||
    customSolutionsImageTabletUrl === null ||
    customSolutionsImageMobileUrl === null ||
    customSolutionsFeatures.length !== 3 ||
    codeBlockHeading === null ||
    codeBlockBody === null ||
    codeBlockButton === null ||
    getStartedTitle === null ||
    getStartedSteps.length !== 4 ||
    systemRequirements.length !== 3 ||
    resourceCards.length !== 3 ||
    exploreSoundcoreTitle === null ||
    exploreSoundcoreButtonText === null ||
    exploreSoundcoreButtonUrl === null ||
    exploreSoundscapeTitle === null ||
    exploreSoundscapeButtonText === null ||
    exploreSoundscapeButtonUrl === null
  ) {
    return null
  }

  return {
    hero: {
      pillText: heroPillText,
      titleText: heroTitleText,
      description: heroDescription,
    },
    customSolutions: {
      title: customSolutionsTitle,
      subtitle: customSolutionsSubtitle,
      imageDesktopUrl: customSolutionsImageDesktopUrl,
      imageTabletUrl: customSolutionsImageTabletUrl,
      imageMobileUrl: customSolutionsImageMobileUrl,
      features: customSolutionsFeatures,
    },
    codeBlock: {
      heading: codeBlockHeading,
      body: codeBlockBody,
      button: codeBlockButton,
    },
    getStarted: {
      title: getStartedTitle,
      steps: getStartedSteps,
      systemRequirements,
      resourceCards,
    },
    exploreProducts: {
      soundcoreTitle: exploreSoundcoreTitle,
      soundcoreButtonText: exploreSoundcoreButtonText,
      soundcoreButtonUrl: exploreSoundcoreButtonUrl,
      soundscapeTitle: exploreSoundscapeTitle,
      soundscapeButtonText: exploreSoundscapeButtonText,
      soundscapeButtonUrl: exploreSoundscapeButtonUrl,
    },
  }
}

export const fetchSdkContentFromStrapi = async (
  options: FetchSdkContentOptions = {},
): Promise<SdkPageCmsContent | null> => {
  const response = await fetch(buildStrapiRequestUrl(SDK_PAGE_ENDPOINT, options.previewToken), {
    signal: options.signal,
    headers: buildStrapiRequestHeaders(options.previewToken),
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch SDK CMS content: ${response.status}`)
  }

  const content = mapSdkPageContent(await response.json())

  if (!content) {
    throw new Error('SDK CMS content missing required fields')
  }

  return content
}
